import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setSessionActive } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [loading, setLoading] = useState(true);

  // Keep the api-layer session flag in sync so the global 401 handler only redirects
  // when a genuine session is lost (not on anonymous/public 401s).
  useEffect(() => { setSessionActive(Boolean(user)); }, [user]);

  useEffect(() => {
    let active = true;
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => active && setUser(data))
      .catch(() => active && setUser(false))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const login = useCallback(async (email, password, recaptchaToken) => {
    const { data } = await api.post("/auth/login", { email, password, recaptcha_token: recaptchaToken });
    setUser(data.user);
    return data.user;
  }, []);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/google/session", null, {
      headers: { "X-Session-ID": sessionId },
    });
    setUser(data.user);
    return { user: data.user, isNew: Boolean(data.is_new) };
  }, []);

  const register = useCallback(async (payload) => {
    // Enumeration-safe registration returns no session — the user signs in afterward.
    const { data } = await api.post("/auth/register", payload);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed; clearing local session anyway.", e); }
    // Shared-browser hygiene: clear cached per-user UI state so the next person
    // starts clean. Browser-level prefs (language, cookie consent) are preserved.
    try {
      Object.keys(localStorage)
        .filter((k) => k === "fab_greeted" || k.startsWith("fab_lab_done"))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) { /* localStorage unavailable — nothing to clear */ }
    setUser(false);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, googleSession, updateUser }),
    [user, loading, login, register, logout, googleSession, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
